import Vue from 'vue';
import {Article, ArticlePartContent, ArticleQuestion, KeywordForAi} from '@/model/Article';
import {KeywordType} from '@/enum/keyword_type';
import {stripTagsAndEntities} from '@/utils/string';
import {Store} from '@/plugins/Store';
import {focusWorkspaceAnchor} from '@/utils/scroll';
import {MAX_AI_KEYWORDS} from '@/utils/Article';
import {canTurnOnLucySlot, resolveLucyIntent} from '@/service/keywordLucySelection';
import {emptyAiWritingBrief} from '@/service/aiWritingBrief';
import {humanizeAiAnswerMarkdown} from '@/service/aiBriefCopy';
import type {AiWritingBrief} from '@/model/AiWritingBrief';
import type {ArticleToneId, GeoTemplateId} from '@/config/WritingBrief';
import {normalizeGeoTemplate} from '@/config/WritingBrief';

export type LiveBriefPlatform = 'googleAio' | 'chatgpt' | 'gemini' | 'perplexity';

export const LIVE_BRIEF_PLATFORMS: LiveBriefPlatform[] = ['googleAio', 'chatgpt', 'gemini', 'perplexity'];

export const LIVE_PLATFORM_TEMPLATE: Record<LiveBriefPlatform, GeoTemplateId> = {
    googleAio: 'aiOverviews',
    chatgpt: 'chatgpt',
    gemini: 'gemini',
    perplexity: 'perplexity',
};

export const LIVE_PLATFORM_TONE: Record<LiveBriefPlatform, ArticleToneId> = {
    googleAio: 'professional',
    chatgpt: 'friendly',
    gemini: 'expert',
    perplexity: 'expert',
};

export function liveCardPlatforms(_answers?: Array<{platform?: string}> | null): LiveBriefPlatform[] {
    return LIVE_BRIEF_PLATFORMS.slice();
}

export function normalizeBriefText(value: string | null | undefined): string {
    return stripTagsAndEntities(value || '')
        .replace(/[¿?？]/g, '')
        .replace(/[„“”"']/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

export function textsMatch(a: string, b: string): boolean {
    const left = normalizeBriefText(a);
    const right = normalizeBriefText(b);
    if (!left || !right) {
        return false;
    }
    if (left === right) {
        return true;
    }
    return left.length >= 12 && right.length >= 12 && (left.includes(right) || right.includes(left));
}

export function looksLikeQuestion(text: string): boolean {
    const value = stripTagsAndEntities(text).replace(/\s+/g, ' ').trim();
    if (!value) {
        return false;
    }
    if (/[?¿？]/.test(value)) {
        return true;
    }
    return /^(co |jak |pro[cč] |kdy |kde |kdo |kter |kolik |kam |[cč][ií]m |zda |je |jsou |k [cč]emu |na co |who |what |how |why |when |which |does |do |did |is |are |can |should |will |would |could )/i.test(value + ' ');
}

export function headingAnchor(part: string, tag: 'h' | 'p', index: number): string {
    return '#' + part + tag + 'w' + (index ? String(index) : '');
}

export function faqAnchor(index: number): string {
    return '#question-' + (index ? String(index) : '');
}

export function shortHint(text: string, max = 220): string {
    const value = humanizeAiAnswerMarkdown(text)
        .replace(/\s+/g, ' ')
        .trim();
    if (value.length <= max) {
        return value;
    }
    return value.slice(0, max).replace(/\s+\S*$/, '') + '…';
}

export function findHeadingIndex(article: Article, text: string): number {
    return (article.h2 || []).findIndex((block) => textsMatch(block.h, text));
}

export function findFaqIndex(article: Article, text: string): number {
    return (article.questions || []).findIndex((item) => textsMatch(item.question, text));
}

export function textContainsPhrase(haystack: string, phrase: string): boolean {
    const text = normalizeBriefText(haystack);
    const needle = normalizeBriefText(phrase);
    return !!text && !!needle && text.includes(needle);
}

export function findEntityLocation(article: Article, entity: string): {part: string; index: number; tag: 'h' | 'p'} | null {
    if (article.h1 && article.h1[0] && textContainsPhrase(article.h1[0].p, entity)) {
        return {part: 'h1', index: 0, tag: 'p'};
    }
    for (let level = 1; level <= 6; level++) {
        const part = ('h' + level) as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        const blocks = article[part] || [];
        for (let index = 0; index < blocks.length; index++) {
            if (part === 'h1' && index === 0) {
                continue;
            }
            if (textContainsPhrase(blocks[index].p, entity)) {
                return {part, index, tag: 'p'};
            }
            if (textContainsPhrase(blocks[index].h, entity)) {
                return {part, index, tag: 'h'};
            }
        }
    }
    return null;
}

export function insertPhraseIntoHtml(html: string, phrase: string): string {
    if (!stripTagsAndEntities(html)) {
        return phrase;
    }
    if (textContainsPhrase(html, phrase)) {
        return html;
    }
    const injection = ' ' + phrase;
    if (/<\/p>\s*$/i.test(html)) {
        return html.replace(/<\/p>\s*$/i, injection + '</p>');
    }
    return html.replace(/\s*$/, '') + injection;
}

function commitKeywordsForAi(next: KeywordForAi[]): void {
    Store.commit('setKeywordsForAi', next);
    Store.getters.article.keywordsForAi = next;
}

function ensureKeywordsForAi(keyword: string, type: number): void {
    const current = [...(Store.getters.articleKeywordsForAi || [])] as KeywordForAi[];
    const existing = current.find((item) => textsMatch(item.keyword, keyword));
    if (existing) {
        if (type === KeywordType.QUESTION) {
            existing.type = KeywordType.QUESTION;
        }
        commitKeywordsForAi(current);
        return;
    }
    const candidate: KeywordForAi = {
        keyword,
        type,
        intent: type === KeywordType.QUESTION ? 'faq' : 'section',
    };
    if (!canTurnOnLucySlot(current, resolveLucyIntent(candidate))) {
        return;
    }
    current.push(candidate);
    commitKeywordsForAi(current);
}

function patchWritingBrief(partial: Partial<AiWritingBrief>): void {
    const article = Store.getters.article as Article;
    const current = article.aiWritingBrief || emptyAiWritingBrief();
    Vue.set(article, 'aiWritingBrief', {...current, ...partial});
}

export function addHeadingIfMissing(text: string): {added: boolean; index: number} {
    const article = Store.getters.article as Article;
    const existing = findHeadingIndex(article, text);
    if (existing !== -1) {
        focusWorkspaceAnchor(headingAnchor('h2', 'h', existing));
        return {added: false, index: existing};
    }
    const index = (article.h2 || []).length;
    Store.commit('insertArticleContentSubPart', {
        contentPart: 'h2',
        index,
        aiLoading: false,
        keepContent: false,
    });
    Store.commit('setArticleContent', {
        contentPart: 'h2',
        index,
        contentKey: 'h',
        value: text,
    });
    ensureKeywordsForAi(text, KeywordType.RELATED);
    window.setTimeout(() => focusWorkspaceAnchor(headingAnchor('h2', 'h', index)), 80);
    return {added: true, index};
}

export function toggleFaqQuestion(text: string): {inFaq: boolean; index: number} {
    const article = Store.getters.article as Article;
    const existing = findFaqIndex(article, text);
    if (existing !== -1) {
        const keepRelated = findHeadingIndex(article, text) !== -1;
        Store.commit('removeArticleQuestion', existing);
        const current = [...(Store.getters.articleKeywordsForAi || [])] as KeywordForAi[];
        const index = current.findIndex((item) => textsMatch(item.keyword, text));
        if (index !== -1) {
            if (keepRelated) {
                current[index] = {keyword: current[index].keyword, type: KeywordType.RELATED};
            } else {
                current.splice(index, 1);
            }
            commitKeywordsForAi(current);
        }
        window.setTimeout(() => focusWorkspaceAnchor('#answers'), 80);
        return {inFaq: false, index: existing};
    }
    const emptyIndex = (article.questions || []).findIndex((item: ArticleQuestion) =>
        !stripTagsAndEntities(item.question) && !stripTagsAndEntities(item.answer)
    );
    const index = emptyIndex !== -1 ? emptyIndex : (article.questions || []).length;
    if (emptyIndex === -1) {
        Store.commit('insertArticleQuestion', {index, aiLoading: false});
    }
    Store.commit('setArticleQuestion', {
        index,
        questionKey: 'question',
        value: text,
    });
    ensureKeywordsForAi(text, KeywordType.QUESTION);
    window.setTimeout(() => focusWorkspaceAnchor(faqAnchor(index)), 80);
    return {inFaq: true, index};
}

export function insertFaqAnswerHint(question: string, hint: string): {index: number; filled: boolean} {
    const text = String(hint || '').replace(/\s+/g, ' ').trim();
    let index = findFaqIndex(Store.getters.article as Article, question);
    if (index === -1) {
        index = toggleFaqQuestion(question).index;
    }
    const article = Store.getters.article as Article;
    const current = stripTagsAndEntities(article.questions?.[index]?.answer || '');
    if (!text || current) {
        window.setTimeout(() => focusWorkspaceAnchor(faqAnchor(index)), 80);
        return {index, filled: false};
    }
    Store.commit('setArticleQuestion', {
        index,
        questionKey: 'answer',
        value: text,
    });
    window.setTimeout(() => focusWorkspaceAnchor(faqAnchor(index)), 80);
    return {index, filled: true};
}

export function addEntityToIntro(entity: string): {added: boolean; anchor: string} {
    const article = Store.getters.article as Article;
    const existing = findEntityLocation(article, entity);
    if (existing) {
        const anchor = headingAnchor(existing.part, existing.tag, existing.index);
        focusWorkspaceAnchor(anchor);
        return {added: false, anchor};
    }
    if (!article.h1 || article.h1.length === 0) {
        Store.commit('insertArticleContentSubPart', {
            contentPart: 'h1',
            index: 0,
            aiLoading: false,
            keepContent: false,
        });
    }
    const currentHtml = Store.getters.article.h1[0]?.p || '';
    Store.commit('setArticleContent', {
        contentPart: 'h1',
        index: 0,
        contentKey: 'p',
        value: insertPhraseIntoHtml(currentHtml, entity),
    });
    const facts = [...(article.aiWritingBrief?.selectedFacts || [])];
    if (!facts.some((item) => textsMatch(item, entity))) {
        facts.push(entity);
        patchWritingBrief({selectedFacts: facts.slice(0, 12)});
    }
    const anchor = headingAnchor('h1', 'p', 0);
    window.setTimeout(() => focusWorkspaceAnchor(anchor), 80);
    return {added: true, anchor};
}

export function applyGeoTemplate(id: GeoTemplateId): void {
    const article = Store.getters.article as Article;
    Vue.set(article, 'geoTemplate', normalizeGeoTemplate(id));
}

export function applyTone(id: ArticleToneId): void {
    const article = Store.getters.article as Article;
    Vue.set(article, 'tone', id);
}

export function keepAnswerShape(directive?: string): void {
    const current = Store.getters.article.aiWritingBrief || emptyAiWritingBrief();
    const introDirectives = [...(current.introDirectives || [])];
    if (directive && !introDirectives.some((item) => textsMatch(item, directive))) {
        introDirectives.push(directive);
    }
    patchWritingBrief({
        keepAnswerShape: true,
        introDirectives: introDirectives.slice(0, 8),
    });
}

export function syncSelectedBriefIntoKeywords(article: Article): void {
    if (article.isAnalyze) {
        return;
    }
    const next = [...(Store.getters.articleKeywordsForAi || [])] as KeywordForAi[];
    const add = (keyword: string, type: number) => {
        if (!keyword) {
            return;
        }
        const found = next.find((item) => textsMatch(item.keyword, keyword));
        if (found) {
            if (type === KeywordType.QUESTION) {
                found.type = KeywordType.QUESTION;
            }
            return;
        }
        if (next.length < MAX_AI_KEYWORDS) {
            next.push({keyword, type});
        }
    };
    (article.h2 || []).forEach((block: ArticlePartContent) => {
        const heading = stripTagsAndEntities(block.h);
        if (heading) {
            add(heading, looksLikeQuestion(heading) ? KeywordType.QUESTION : KeywordType.RELATED);
        }
    });
    (article.questions || []).forEach((item: ArticleQuestion) => {
        const question = stripTagsAndEntities(item.question);
        if (question) {
            add(question, KeywordType.QUESTION);
        }
    });
    commitKeywordsForAi(next);
}
